/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-shadow */
import React from "react";

import { EngineNames, PlatformNames } from "../../../../constants";
import { formatDateFieldValue } from "../../../../helpers/dateHelpers";
import { PitchInformationAboutYourGameDataType } from "../../../../models/forms";
import {
  BusinessModelOptions,
  ContainsNftOrBlockchainOptions,
  DevelopmentStageOptions,
  GameEngineOptions,
  GenreOptions,
  HasTheGameBeenAnnouncedOptions,
  OnlineMultiplayerOptions,
  ReleasedForOtherPlatformsOptions,
  ReleasedPlatformsOptions,
  SensitiveContentOptions,
  TargetedPlatformsOptions,
} from "../../../../models/pitchUxModels";
import { ReadOnlyFormItem } from "../appInfoModelsAndHelpers";
import { ReadOnlyForm } from "../ReadOnlyForm";

type ReadOnlyPitchAboutYourGameProps = {
  dataInForm: PitchInformationAboutYourGameDataType;
};

const nftOrBlockchainMap = new Map<string, string>([
  [ContainsNftOrBlockchainOptions.Yes, "yes"],
  [ContainsNftOrBlockchainOptions.No, "no"],
  [ContainsNftOrBlockchainOptions.Unknown, "unknownString"],
]);

const stageOfDevelopmentMap = new Map<string, string>([
  [DevelopmentStageOptions.GameConcept, "gameConcept"],
  [DevelopmentStageOptions.Prototype, "prototype"],
  [DevelopmentStageOptions.Alpha, "alpha"],
  [DevelopmentStageOptions.Beta, "beta"],
  [DevelopmentStageOptions.ClosedBeta, "closedBeta"],
  [DevelopmentStageOptions.OpenBeta, "openBeta"],
  [DevelopmentStageOptions.ReadyToLaunch, "readyToLaunch"],
  [DevelopmentStageOptions.EarlyAccess, "earlyAccess"],
  [DevelopmentStageOptions.Launched, "launched"],
]);

const releasedForOtherPlatformsMap = new Map<string, string>([
  [ReleasedForOtherPlatformsOptions.Yes, "yes"],
  [ReleasedForOtherPlatformsOptions.No, "no"],
]);

const releasedPlatformsMap = new Map<string, string>([
  [ReleasedPlatformsOptions.Sony, PlatformNames.SonyPlaystation],
  [ReleasedPlatformsOptions.Steam, PlatformNames.Steam],
  [ReleasedPlatformsOptions.NintendoSwitch, PlatformNames.NintendoSwitch],
  [ReleasedPlatformsOptions.EpicStore, PlatformNames.EpicStore],
  [ReleasedPlatformsOptions.Other, "other"],
]);

const hasGameBeenAnnouncedMap = new Map<string, string>([
  [HasTheGameBeenAnnouncedOptions.Yes, "yes"],
  [HasTheGameBeenAnnouncedOptions.No, "no"],
]);

const targetedPlatformsMap = new Map<string, string>([
  [TargetedPlatformsOptions.SonyPlaystation, PlatformNames.SonyPlaystation],
  [TargetedPlatformsOptions.Steam, PlatformNames.Steam],
  [TargetedPlatformsOptions.NintendoSwitch, PlatformNames.NintendoSwitch],
  [TargetedPlatformsOptions.EpicStore, PlatformNames.EpicStore],
  [TargetedPlatformsOptions.Other, "other"],
]);

const genreMap = new Map<string, string>([
  [GenreOptions.Action, "action"],
  [GenreOptions.Adventure, "adventure"],
  [GenreOptions.Arcade, "arcade"],
  [GenreOptions.BattleRoyale, "battleRoyale"],
  [GenreOptions.FirstPersonThirdPersonShooter, "firstPersonThirdPersonShooter"],
  [GenreOptions.MMORPG, "mmorpg"],
  [GenreOptions.MOBA, "moba"],
  [GenreOptions.NarrativeStory, "narrativeStory"],
  [GenreOptions.RolePlayingGame, "rolePlayingGame"],
  [GenreOptions.Sandbox, "sandbox"],
  [GenreOptions.Simulation, "simulation"],
  [GenreOptions.SurvivalHorror, "survivalHorror"],
  [GenreOptions.Other, "other"],
]);

const gameEnginesMap = new Map<string, string>([
  [GameEngineOptions.Unity, EngineNames.Unity],
  [GameEngineOptions.UnrealEngine, EngineNames.UnrealEngine],
  [GameEngineOptions.Godot, EngineNames.Godot],
  [GameEngineOptions.Other, "other"],
]);

const businessModelMap = new Map<string, string>([
  [BusinessModelOptions.Premium, "premium"],
  [BusinessModelOptions.PremiumPaidDLC, "premiumPaidDLC"],
  [BusinessModelOptions.PremiumFreeDLC, "premiumFreeDLC"],
  [BusinessModelOptions.FreeToPlay, "freeToPlay"],
  [BusinessModelOptions.GamesAsAService, "gamesAsAService"],
  [BusinessModelOptions.Other, "other"],
]);

const gameIncludesOnlineMultiplayerMap = new Map<string, string>([
  [OnlineMultiplayerOptions.Yes, "yes"],
  [OnlineMultiplayerOptions.No, "no"],
]);

const sensitiveContentMap = new Map<string, string>([
  [SensitiveContentOptions.Yes, "yes"],
  [SensitiveContentOptions.No, "no"],
  [SensitiveContentOptions.Maybe, "maybe"],
  [SensitiveContentOptions.Other, "other"],
]);

const PitchAboutYourGameConfig: Map<string, ReadOnlyFormItem> = new Map([
  ["gameTitle", {}],
  [
    "containsNftOrBlockchain",
    {
      title: "pitchAboutYourGameContainsNftOrBlockchainTitle",
      mapper: (
        data: PitchInformationAboutYourGameDataType,
        localizedStrings: Map<string, string>
      ) => localizedStrings.get(nftOrBlockchainMap.get(data.containsNftOrBlockchain) ?? ""),
    },
  ],
  [
    "stageOfDevelopment",
    {
      title: "pitchAboutYourGameStageOfDevelopmentTitle",
      mapper: (
        data: PitchInformationAboutYourGameDataType,
        localizedStrings: Map<string, string>
      ) => localizedStrings.get(stageOfDevelopmentMap.get(data.stageOfDevelopment) ?? ""),
    },
  ],
  [
    "releasedForOtherPlatforms",
    {
      title: "pitchAboutYourGameHasTheGameBeenLaunchedTitle",
      mapper: (
        data: PitchInformationAboutYourGameDataType,
        localizedStrings: Map<string, string>
      ) =>
        localizedStrings.get(
          releasedForOtherPlatformsMap.get(data.releasedForOtherPlatforms) ?? ""
        ),
    },
  ],
  [
    "releasedPlatforms",
    {
      title: "pitchAboutReleasedPlatformsTitle",
      mapper: (
        data: PitchInformationAboutYourGameDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.releasedPlatforms.map(element => {
          const releasedPlatform = releasedPlatformsMap.get(element);
          return releasedPlatform === "other"
            ? localizedStrings.get(releasedPlatform)
            : releasedPlatform ?? "";
        }),
    },
  ],
  [
    "releasedPlatformsDate",
    {
      title: "pitchAboutYourGameContainsReleasedPlatformsDateTitle",
      mapper: (data: PitchInformationAboutYourGameDataType) =>
        data.releasedPlatformsDate ? formatDateFieldValue(data.releasedPlatformsDate) : undefined,
    },
  ],
  [
    "announced",
    {
      title: "pitchAboutYourGameHasTheGameBeenAnnouncedTitle",
      mapper: (
        data: PitchInformationAboutYourGameDataType,
        localizedStrings: Map<string, string>
      ) => localizedStrings.get(hasGameBeenAnnouncedMap.get(data.announced) ?? ""),
    },
  ],
  [
    "targetedPlatforms",
    {
      title: "pitchAboutYourGameTargetedPlatformsTitle",
      mapper: (data: PitchInformationAboutYourGameDataType) =>
        data.targetedPlatforms.map(element => targetedPlatformsMap.get(element) ?? ""),
    },
  ],
  [
    "steamReleaseWindow",
    {
      title: "pitchAboutYourGameSteamReleaseWindowTitle",
      mapper: (data: PitchInformationAboutYourGameDataType) =>
        data.steamReleaseWindow ? formatDateFieldValue(data.steamReleaseWindow) : undefined,
    },
  ],
  [
    "xboxReleaseWindow",
    {
      title: "pitchAboutYourGameXboxReleaseWindowTitle",
      mapper: (data: PitchInformationAboutYourGameDataType) =>
        data.xboxReleaseWindow ? formatDateFieldValue(data.xboxReleaseWindow) : undefined,
    },
  ],
  [
    "genre",
    {
      title: "pitchAboutYourGameWhichGameGenreFitsYourGameBestTitle",
      mapper: (
        data: PitchInformationAboutYourGameDataType,
        localizedStrings: Map<string, string>
      ) => data.genre.map(element => localizedStrings.get(genreMap.get(element) ?? "")),
    },
  ],
  [
    "gameEngine",
    {
      title: "pitchAboutYourGameWhichGameEngineTitle",
      mapper: (
        data: PitchInformationAboutYourGameDataType,
        localizedStrings: Map<string, string>
      ) =>
        gameEnginesMap.get(data.gameEngine) === "other"
          ? localizedStrings.get("other")
          : gameEnginesMap.get(data.gameEngine) ?? "",
    },
  ],
  [
    "businessModel",
    {
      title: "pitchAboutYourGameBusinessModelTitle",
      mapper: (
        data: PitchInformationAboutYourGameDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.businessModel.map(element =>
          localizedStrings.get(businessModelMap.get(element) ?? "")
        ),
    },
  ],
  ["gameplayDuration", { title: "pitchAboutYourGameGameplayDurationTitle" }],
  [
    "onlineMultiplayer",
    {
      title: "pitchAboutYourGameOnlineMultiplayerTitle",
      mapper: (
        data: PitchInformationAboutYourGameDataType,
        localizedStrings: Map<string, string>
      ) => localizedStrings.get(gameIncludesOnlineMultiplayerMap.get(data.onlineMultiplayer) ?? ""),
    },
  ],
  ["gameIntroDeckLink", { title: "pitchAboutYourGameLinkGameIntroductionDeckTitle" }],
  ["otherInfoLinks", { title: "pitchAboutYourGameLinkOtherAssetsTitle" }],
  ["buildLinks", { title: "pitchAboutYourGameBuildLinksTitle" }],
  ["videoLinks", { title: "pitchAboutYourGameVideoLinksTitle" }],
  ["gameDescription", { title: "pitchAboutYourGameGameDescriptionTitle" }],
  ["targetAudience", { title: "pitchAboutYourGameTargetAudienceTitle" }],
  ["competitorTitles", { title: "pitchAboutYourGameCompetitorTitlesTitle" }],
  ["uniqueFactor", { title: "pitchAboutYourGameUniqueFactorTitle" }],
  ["feelingsEvoked", { title: "pitchAboutYourGameFeelingsEvokedTitle" }],
  ["pivotalMoment", { title: "pitchAboutYourGamePivotalMomentTitle" }],
  ["postLaunchSupportPlans", { title: "pitchAboutYourGamePostLaunchSupportPlansTitle" }],
  [
    "sensitiveContent",
    {
      title: "pitchAboutYourGameSensitiveContentTitle",
      mapper: (
        data: PitchInformationAboutYourGameDataType,
        localizedStrings: Map<string, string>
      ) => localizedStrings.get(sensitiveContentMap.get(data.sensitiveContent) ?? ""),
    },
  ],
  ["sensitiveContentDetails", { title: "pitchAboutYourGameSensitiveContentDetailsTitle" }],
]);

export const ReadOnlyPitchAboutYourGame = ({ dataInForm }: ReadOnlyPitchAboutYourGameProps) => {
  const extraStringsToLocalize = [
    "yes",
    "no",
    "unknownString",
    "gameConcept",
    "prototype",
    "alpha",
    "beta",
    "closedBeta",
    "openBeta",
    "readyToLaunch",
    "earlyAccess",
    "launched",
    "other",
    "action",
    "adventure",
    "arcade",
    "battleRoyale",
    "firstPersonThirdPersonShooter",
    "mmorpg",
    "moba",
    "narrativeStory",
    "rolePlayingGame",
    "sandbox",
    "simulation",
    "survivalHorror",
    "premium",
    "premiumPaidDLC",
    "premiumFreeDLC",
    "freeToPlay",
    "gamesAsAService",
  ];

  return (
    <ReadOnlyForm
      formConfig={PitchAboutYourGameConfig}
      formData={dataInForm}
      extraStringsToLocalize={extraStringsToLocalize}
    />
  );
};
